import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { usePreventBack } from "../usePreventBack";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import Navbar from "../../../components/molecules/Navbar";
import Footer from "../../../components/molecules/Footer";
import Loader from "../../../components/atoms/Loader";
import panIcon from "../../../images/icons/pan_annimation/main.svg";
import styles from "../index.module.css";

const labelStyle: React.CSSProperties = {
  fontSize: "0.68rem",
  fontWeight: 600,
  color: "#999",
  letterSpacing: "0.7px",
  textTransform: "uppercase",
  marginBottom: "0.3rem",
  display: "block",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.72rem 0.9rem 0.72rem 2.4rem",
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  fontSize: "0.88rem",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "Outfit, sans-serif",
  color: "#333",
  background: "#fff",
};

const iconWrap: React.CSSProperties = {
  position: "absolute",
  left: "0.75rem",
  top: "50%",
  transform: "translateY(-50%)",
  display: "flex",
  alignItems: "center",
  pointerEvents: "none",
};

function EligibilityPanDetails() {
  usePreventBack();
  const navigate = useNavigate();
  const { data } = useParams();
  const location = useLocation();

  const [pan, setPan] = useState("");
  const [dob, setDob] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [skipFetching, setSkipFetching] = useState(false);
  const [error, setError] = useState("");

  // Login flow extras
  const [loginName, setLoginName] = useState("");
  const [skipTracingUserId, setSkipTracingUserId] = useState("");
  const [loginMobile, setLoginMobile] = useState("");

  useEffect(() => {
    if (!data) {
      // Coming from /eligibility/login — no encoded data
      if (!sessionStorage.getItem("eligibility_auth_token")) {
        navigate("/eligibility/login");
        return;
      }
      if (location.state?.mobile) setLoginMobile(location.state.mobile);
      return;
    }
    try {
      const raw = atob(data!);
      // Normalize unquoted keys (e.g. pincode:"500032" → "pincode":"500032")
      const sanitized = raw.replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":');
      const decoded = JSON.parse(sanitized);
      sessionStorage.setItem("eligibility_url_data", JSON.stringify(decoded));
      navigate("/eligibility/login", { replace: true, state: { mobile: decoded.mobile || "" } });
    } catch {
      navigate("/eligibility/login", { replace: true });
    }
  }, [data, navigate]);

  // Back-navigation guard: if user has an active session with a stage > 0, redirect forward
  useEffect(() => {
    if (data) return; // URL entry flow — let the above effect handle it
    const token = sessionStorage.getItem("eligibility_auth_token");
    const stored = sessionStorage.getItem("eligibility_data");
    if (!token || !stored) return;
    try {
      const navData = JSON.parse(stored);
      if (!navData?.applicationId) return;
      axios
        .get(`${process.env.REACT_APP_DASHBOARD_URL}/admin/application/stage?applicationId=${navData.applicationId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          const stage = res?.data?.data?.stage;
          if (!stage || stage === 0) return;
          if (stage === 1) { navigate("/eligibility/income", { replace: true, state: { data: navData } }); return; }
          if (stage === 2 || stage === 3) { navigate("/eligibility/bank-statement", { replace: true, state: { data: navData } }); return; }
          if (stage === 4) { navigate("/eligibility/waiting", { replace: true, state: { data: navData, nextRoute: "/eligibility/digilocker" } }); return; }
          if (stage === 5) { navigate("/eligibility/under-review", { replace: true, state: { data: navData } }); return; }
          if (stage === 8) { navigate("/eligibility/congratulations", { replace: true, state: { data: navData } }); return; }
          if (stage === 9) { navigate("/eligibility/selfie", { replace: true, state: { data: navData } }); return; }
          if (stage === 10) { navigate("/eligibility/waiting", { replace: true, state: { data: navData, fromStage10: true } }); return; }
          if (stage === 11) { navigate("/eligibility/agreement", { replace: true, state: { data: navData } }); return; }
          if (stage === 12) { navigate("/eligibility/emandate", { replace: true, state: { data: navData } }); return; }
          if (stage === 24) { navigate("/eligibility/digilocker", { replace: true, state: { data: navData } }); return; }
          if (stage === 25) { navigate("/eligibility/evaluating", { replace: true, state: { data: navData } }); return; }
          if (stage === 18) { navigate("/eligibility/rejected", { replace: true, state: { data: navData } }); return; }
        })
        .catch(() => {});
    } catch {}
  }, []);


  // ── Skip-tracing: fetch PAN/DOB on mount and prefill if found ──
  useEffect(() => {
    if (data) return;
    const token = sessionStorage.getItem("eligibility_auth_token");
    if (!token) return;
    const urlData = JSON.parse(sessionStorage.getItem("eligibility_url_data") || "{}");
    if (urlData.applicationId) return; // institute-enrolled link — skip-tracing not needed
    const mobile = location.state?.mobile;
    if (!mobile) return;

    setSkipFetching(true);
    axios
      .post(
        `${process.env.REACT_APP_DASHBOARD_URL}/summary/skip-tracing`,
        { mobile },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        const skipped = res?.data?.data;
        if (skipped?.panNumber) setPan(skipped.panNumber);
        if (skipped?.dob) {
          const parts = skipped.dob.split("-");
          if (parts.length === 3) setDob(`${parts[2]}-${parts[1]}-${parts[0]}`);
        }
        if (skipped?.name) setLoginName(skipped.name);
        if (skipped?.userId) setSkipTracingUserId(skipped.userId);
      })
      .catch(() => {})
      .finally(() => setSkipFetching(false));
  }, [data]);

  // ── Institute-enrolled link: skip PAN form if application already exists ──
  useEffect(() => {
    if (data) return; // URL entry flow is handled by the first effect
    const token = sessionStorage.getItem("eligibility_auth_token");
    const urlData = JSON.parse(sessionStorage.getItem("eligibility_url_data") || "{}");
    const { applicationId: urlAppId, userId: urlUserId, mobile: urlMobile } = urlData;
    if (!urlAppId || !urlUserId || !token) return;

    // Store nav data so the back-nav guard can use it too if needed
    sessionStorage.setItem("eligibility_data", JSON.stringify({
      userId: urlUserId,
      applicationId: urlAppId,
      mobile: urlMobile,
    }));

    setChecking(true);

    const checkAndSkip = async () => {
      try {
        const [stageRes, basicRes] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_DASHBOARD_URL}/admin/application/stage?applicationId=${urlAppId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
          axios.get(
            `${process.env.REACT_APP_DASHBOARD_URL}/users/basic-info?searchField=${urlMobile}`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
        ]);

        const stage = stageRes?.data?.data?.stage;
        const stageStatus = stageRes?.data?.data?.status;
        const applications: any[] = basicRes?.data?.data || [];
        const thisApp = applications.find((a: any) => a.applicationId === urlAppId);
        const status = thisApp?.status;

        if ((!stage || stage === 0) && status === "In Submission") {
          const loc = JSON.parse(sessionStorage.getItem("eligibility_location") || "{}");
          try {
            await axios.post(
              `${process.env.REACT_APP_DASHBOARD_URL}/end-user/submit/qec`,
              { userId: urlUserId, latitude: loc.latitude || 0, longitude: loc.longitude || 0, doneBy: "QEC" },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch {
            // proceed even if qec fails
          }
          navigate("/eligibility/income", {
            replace: true,
            state: { data: { userId: urlUserId, applicationId: urlAppId, mobile: urlMobile } },
          });
          return;
        }
        // stage > 0 — the back-nav guard (above) will handle routing via eligibility_data
      } catch {
        // API failed — fall through and show the PAN form normally
      } finally {
        setChecking(false);
      }
    };

    checkAndSkip();
  }, [data, navigate]);

  const handleNext = async () => {
    setError("");
    setLoading(true);
    try {
      const urlData = JSON.parse(sessionStorage.getItem("eligibility_url_data") || "{}");

      const nameParts = loginName.trim().split(" ");
      const fName = nameParts[0] || urlData.firstName || "";
      const lName = nameParts.slice(1).join(" ") || urlData.lastName || "";
      // format dob from YYYY-MM-DD → DD-MM-YYYY for the API
      const dobParts = dob.split("-");
      const formattedDob = dobParts.length === 3
        ? `${dobParts[2]}-${dobParts[1]}-${dobParts[0]}`
        : dob;

      const sessionToken = sessionStorage.getItem("eligibility_auth_token");

      let tokenUserId = "";
      try {
        const decoded: any = jwtDecode(sessionToken || "");
        tokenUserId = decoded?.userId || "";
      } catch {
        // token decode failed, proceed with empty userId
      }

      const body1 = {
        firstName: fName,
        lastName: lName,
        dob: formattedDob,
        gender: "",
        email: "",
        mobile: loginMobile,
        panId: pan,
        aadhaarId: "",
        currentAddress: " ",
        currentCity: " ",
        currentState: " ",
        currentPincode: " ",
        panImage: " ",
        aadhaarFrontImage: " ",
        aadhaarBackImage: " ",
        employmentType: " ",
        employerName: " ",
        salary: 0,
        typeOfBusiness: " ",
        incomePerMonth: 0,
        employmentCity: " ",
        courseFees: urlData.courseFees || 0,
        instituteName: urlData.instituteName || "",
        courseName: urlData.courseName || "",
        channelId: urlData.channelId || 6,
        loanTenure: urlData.loanTenure || 0,
        studentMobile: "",
        userId: tokenUserId,
      };

      const createRes = await axios.post(`${process.env.REACT_APP_DASHBOARD_URL}/pay-later-flow/create`, body1, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });

      const kyc = createRes?.data?.data;
      const newUserId = kyc?.userId || "";
      const newApplicationId = kyc?.application || "";

      // await qec before navigating
      if (newUserId) {
        const loc = JSON.parse(sessionStorage.getItem("eligibility_location") || "{}");
        try {
          await axios.post(`${process.env.REACT_APP_DASHBOARD_URL}/end-user/submit/qec`, {
            userId: newUserId,
            latitude: loc.latitude || 0,
            longitude: loc.longitude || 0,
            doneBy: "QEC",
          }, { headers: { Authorization: `Bearer ${sessionToken}` } });
        } catch (err: any) {
          console.log("end-user/submit/qec error:", err);
        }
      }

      // navigate to waiting page — credit bureau will be called there
      navigate("/eligibility/waiting", {
        state: {
          data: { userId: newUserId, applicationId: newApplicationId, mobile: loginMobile },
          creditBureau: { name: fName, mobile: loginMobile, pan, applicationId: newApplicationId },
          fromCreditBureau: true,
        },
      });
    } catch (e: any) {
      const errData = e?.response?.data;
      const specificErr = errData?.data || "";
      const isGenericBackendError =
        specificErr.includes("Request failed with status code 400") ||
        specificErr === "Invalid PAN number";
      setError(
        isGenericBackendError
          ? "Please check PAN or DOB entered"
          : specificErr || errData?.message || "Oops! There is an issue at our end. Please contact us."
      );
    } finally {
      setLoading(false);
    }
  };

  if (checking || skipFetching) {
    return (
      <div className={styles.body}>
        <div className={styles.backdrop}>
          <Navbar />
          <div className={styles.main} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Loader />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.body}>
      <div className={styles.backdrop}>
        <Navbar />
        <div className={styles.main}>
          <div className={styles.cardWrapper}>
            <div className={styles.container}>
              <div className={styles.cardHeader}>
                <img src={panIcon} alt="" />
                <p className={styles.cardTitle}>PAN Details</p>
              </div>
              <div className={styles.content} style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>

                {/* PAN Number */}
                <div>
                  <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "#333", marginBottom: "0.35rem", display: "block" }}>
                    PAN Number
                  </span>
                  <div style={{ position: "relative" }}>
                    <span style={iconWrap}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                        <rect x="2" y="5" width="20" height="14" rx="2" stroke="#c0c0c0" strokeWidth="1.8" />
                        <circle cx="8" cy="12" r="2" stroke="#c0c0c0" strokeWidth="1.5" />
                        <path d="M13 10H18M13 14H16" stroke="#c0c0c0" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </span>
                    <input
                      style={{ ...inputStyle, textTransform: "uppercase", letterSpacing: "0.5px" }}
                      placeholder="E.G. ABCDE1234F"
                      value={pan}
                      onChange={(e) => setPan(e.target.value)}
                      maxLength={10}
                    />
                  </div>
                  <span style={{ fontSize: "0.72rem", color: "#bbb", marginTop: "0.3rem", display: "block" }}>
                    Your PAN will be verified
                  </span>
                </div>

                {/* Date of Birth */}
                <div>
                  <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "#333", marginBottom: "0.35rem", display: "block" }}>
                    Date of Birth
                  </span>
                  <div style={{ position: "relative" }}>
                    <span style={iconWrap}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="5" width="18" height="16" rx="2" stroke="#c0c0c0" strokeWidth="1.8" />
                        <path d="M3 10H21" stroke="#c0c0c0" strokeWidth="1.6" />
                        <path d="M8 3V7M16 3V7" stroke="#c0c0c0" strokeWidth="1.8" strokeLinecap="round" />
                      </svg>
                    </span>
                    <input
                      style={inputStyle}
                      type="date"
                      value={dob}
                      onChange={(e) => setDob(e.target.value)}
                    />
                  </div>
                  <span style={{ fontSize: "0.72rem", color: "#bbb", marginTop: "0.3rem", display: "block" }}>
                    As per your PAN card
                  </span>
                </div>

                {/* Info note */}
                <div style={{
                  display: "flex", alignItems: "flex-start", gap: "0.55rem",
                  background: "#fff9f9", border: "1px solid #fde8e8",
                  borderRadius: "8px", padding: "0.65rem 0.75rem",
                }}>
                  <div style={{
                    width: "26px", height: "26px", borderRadius: "50%",
                    background: "#fde8e8", display: "flex", alignItems: "center",
                    justifyContent: "center", flexShrink: 0,
                  }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2L3 7V12C3 16.55 6.84 20.74 12 22C17.16 20.74 21 16.55 21 12V7L12 2Z" fill="#d32028" opacity="0.15" stroke="#d32028" strokeWidth="1.6" />
                      <path d="M9 12L11 14L15 10" stroke="#d32028" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <span style={{ fontSize: "0.66rem", color: "#888", lineHeight: 1.55 }}>
                    Your PAN and date of birth are used only for identity verification.
                  </span>
                </div>

                {error && (
                  <p style={{ color: "#d32028", fontSize: "0.85rem", marginTop: "0.1rem", marginBottom: 0 }}>
                    {error}
                  </p>
                )}
              </div>
              {loading && <Loader />}
            </div>
            <div style={{ padding: "0rem 1.25rem 1rem", display: "flex", flexDirection: "row", alignItems: "center",justifyContent: "center", gap: "0.6rem" }}>
              <button
                onClick={handleNext}
                disabled={loading}
                style={{
                  width: "65%",
                  padding: "0.8rem 1rem",
                  background: loading ? "#aaa" : "#d32028",
                  color: "#fff",
                  border: "none",
                  borderRadius: "60px",
                  fontSize: "0.9rem",
                  fontWeight: 700,
                  fontFamily: "Outfit, sans-serif",
                  cursor: loading ? "not-allowed" : "pointer",
                  letterSpacing: "0.3px",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                }}
              >
                {loading ? "Please wait..." : (
                  <>
                    Save & Continue
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12H19M13 6L19 12L13 18" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </>
                )}
              </button>
              {process.env.REACT_APP_CURRENT !== 'prod' && (
                <button
                  onClick={() => navigate("/eligibility/income", { state: { data: { userId: "", applicationId: "", mobile: loginMobile } } })}
                  style={{ background: "none", border: "none", color: "#bbb", fontSize: "0.78rem", cursor: "pointer", fontFamily: "Outfit, sans-serif", textDecoration: "underline" }}
                >
                  Skip
                </button>
              )}
            </div>
          <Footer light />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EligibilityPanDetails;
